import { useEffect, useState } from 'react';

type NetworkInformationLike = {
  saveData?: boolean;
  effectiveType?: string;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
  addListener?: (listener: () => void) => void;
  removeListener?: (listener: () => void) => void;
};

type DeviceProfile = {
  isMobile: boolean;
  prefersSaveData: boolean;
  isSlowConnection: boolean;
};

const MOBILE_QUERY = '(max-width: 768px)';
const SLOW_CONNECTION_TYPES = new Set(['slow-2g', '2g']);

const getConnection = (): NetworkInformationLike | null => {
  if (typeof navigator === 'undefined') return null;
  const candidate = navigator as Navigator & { connection?: NetworkInformationLike };
  return candidate.connection ?? null;
};

const evaluateProfile = (): DeviceProfile => {
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      prefersSaveData: false,
      isSlowConnection: false,
    };
  }

  const mediaQuery = typeof window.matchMedia === 'function' ? window.matchMedia(MOBILE_QUERY) : undefined;
  const fallbackIsMobile = typeof window.innerWidth === 'number' ? window.innerWidth <= 768 : false;
  const isMobile = mediaQuery?.matches ?? fallbackIsMobile;

  const connection = getConnection();
  const effectiveType = connection?.effectiveType ?? '';

  return {
    isMobile,
    prefersSaveData: Boolean(connection?.saveData),
    isSlowConnection: SLOW_CONNECTION_TYPES.has(effectiveType),
  };
};

export const useDeviceProfile = (): DeviceProfile => {
  const [profile, setProfile] = useState<DeviceProfile>(() => evaluateProfile());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateProfile = () => {
      setProfile(evaluateProfile());
    };

    const mediaQuery = typeof window.matchMedia === 'function' ? window.matchMedia(MOBILE_QUERY) : undefined;
    const connection = getConnection();

    updateProfile();

    if (mediaQuery) {
      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', updateProfile);
      } else if (typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(updateProfile);
      }
    }

    if (connection) {
      if (typeof connection.addEventListener === 'function') {
        connection.addEventListener('change', updateProfile);
      } else if (typeof connection.addListener === 'function') {
        connection.addListener(updateProfile);
      }
    }

    return () => {
      if (mediaQuery) {
        if (typeof mediaQuery.removeEventListener === 'function') {
          mediaQuery.removeEventListener('change', updateProfile);
        } else if (typeof mediaQuery.removeListener === 'function') {
          mediaQuery.removeListener(updateProfile);
        }
      }

      if (connection) {
        if (typeof connection.removeEventListener === 'function') {
          connection.removeEventListener('change', updateProfile);
        } else if (typeof connection.removeListener === 'function') {
          connection.removeListener(updateProfile);
        }
      }
    };
  }, []);

  return profile;
};

export default useDeviceProfile;
